'use client';

/**
 * AuthErrorHandler
 *
 * Client-side utility component that listens for authentication-related
 * error codes in the `error[code]` query parameter (e.g. after redirects
 * from password reset, email verification, or social auth).
 *
 * When an error code is present, it:
 * - Maps the backend error code to translated title/description messages.
 * - Shows a toast with the corresponding error message.
 * - Cleans the URL by removing the `error[code]` parameter via router.replace,
 *   preventing the error toast from re-appearing on subsequent navigations.
 *
 * The component renders `null` and is intended to be mounted once at a
 * high level (e.g. inside the main application layout).
 */

import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import Toast from '@/components/ui/Toast/toast';
import { usePathname, useRouter } from '@/i18n/routing';

const ERROR_PARAM_KEY = 'error[code]';

const ERROR_MESSAGE_KEYS: Record<string, { titleKey: string; descriptionKey: string }> = {
  INVALID_SECURITY_TOKEN_ERROR: {
    titleKey: 'invalid-security-token.title',
    descriptionKey: 'invalid-security-token.description',
  },
  UNKNOWN_AUTH_ERROR: {
    titleKey: 'unknown-auth-error.title',
    descriptionKey: 'unknown-auth-error.description',
  },
};

export function AuthErrorHandler() {
  const t = useTranslations('auth-errors');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const errorCode = searchParams.get(ERROR_PARAM_KEY);

    if (!errorCode) {
      return;
    }

    const keys = ERROR_MESSAGE_KEYS[errorCode] ?? {
      titleKey: 'fallback-unknown-error.title',
      descriptionKey: 'fallback-unknown-error.description',
    };

    Toast({
      type: 'error',
      title: t(keys.titleKey),
      description: t(keys.descriptionKey),
    });

    // Clean the error param from the URL so it doesn't persist on navigation
    const params = new URLSearchParams(searchParams.toString());
    params.delete(ERROR_PARAM_KEY);

    const nextUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname;

    router.replace(nextUrl);
  }, [pathname, router, searchParams]);

  return null;
}
